import React from "react";
import Button from "@amzn/awsui-components-react-v3/polaris/button";
import { useStepContext } from "../Wizard/WizardStepContextProvider";
import styles from "../../workflow/workflow.module.css";
import { SpaceBetween } from "@amzn/awsui-components-react-v3";

interface WizardFooterProps {
  onCancel?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  previousButtonText?: string;
  nextButtonText?: string;
  isLoading?: boolean;
  nextDisabled?: boolean;
}

/**
 * Component that creates the footer for the Wizard.
 *
 * @param {WizardFooterProps} {
 *   onCancel,
 *   onPrevious,
 *   onNext,
 *   previousButtonText = "Previous",
 *   nextButtonText = "Next",
 * }
 * @returns {JSX.Element}
 */
const WizardFooter: React.FC<WizardFooterProps> = ({
  onCancel,
  onPrevious,
  onNext,
  previousButtonText = "Previous",
  nextButtonText = "Next",
  isLoading = false,
  nextDisabled,
}: WizardFooterProps): JSX.Element => {
  const {
    currentStepNumber,
    minStepNumber,
    maxStepNumber,
    setStepNumber,
    onCancel: onCancelWorkflowDefault,
  } = useStepContext();

  const { defaultPrevious, defaultNext, defaultCancel } = {
    defaultCancel: () => onCancelWorkflowDefault(true),
    defaultPrevious: () => setStepNumber((s) => s - 1),
    defaultNext: () => {
      if (currentStepNumber < maxStepNumber) {
        setStepNumber((s) => s + 1);
      }
    },
  };

  return (
    <div className={styles["workflow-footer"]}>
      <SpaceBetween direction="horizontal" size="l">
        <Button
          ariaLabel="workflow cancel button"
          variant="link"
          onClick={onCancel || defaultCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        {currentStepNumber > minStepNumber && (
          <Button
            className="prev-step-btn"
            onClick={onPrevious || defaultPrevious}
            disabled={isLoading}
          >
            {previousButtonText}
          </Button>
        )}
        <Button
          className="next-step-btn"
          variant="primary"
          onClick={onNext || defaultNext}
          loading={isLoading}
          disabled={!!nextDisabled}
        >
          {nextButtonText}
        </Button>
      </SpaceBetween>
    </div>
  );
};

export default WizardFooter;
